import { countryNames } from '@/ui/words';
import { useState } from 'react';
import { Link } from 'react-router';
import { failureOf } from '@/api/errors';
import { useApproveCampaign, useCampaign, useCampaigns, useCreateCampaign, useEndCampaign, useMarkets, type CampaignTerms } from '@/api/hooks';
import type { WireCampaign } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { Dialog } from '@/ui/Dialog';
import { Badge, Button, Field, Kicker, Skeleton, TextInput } from '@/ui/primitives';
import { FactList, RecordHeader, Table, TokenPanel, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { CAMPAIGN_CHIP, CAMPAIGN_END_WORDS } from './words';
import { Sequences } from './Sequences';
import { Textarea } from '@/ui/fields';
import { useWho } from '@/ui/who';
import { explainCampaign } from './explain';
import { localIsoDate } from '@/today/dates';

/**
 * Campaigns (FR-OUT-73; ADR-0079, ADR-0086).
 *
 * A campaign is what a person approves once so the department's agent may
 * send first emails without a person pressing send on each: one template,
 * the countries, which restaurants, how many, until when — thirty days at
 * most. Approving it is the department lead's or a superadmin's, under a
 * single-use token, and only after a person has sent one of the agent's
 * drafts and watched it arrive under its name. Ending one needs no token.
 *
 * Even approved, a campaign confers nothing on its own: every email is
 * judged again as it leaves — the level the department has earned, the
 * hour in the recipient's country, who said stop. The screen says so.
 */
export function Campaigns() {
  const me = useMeOrThrow();
  const list = useCampaigns(me.department);
  const [openId, setOpenId] = useState<string | null>(null);
  const mayDecide = me.isSuperadmin || me.leads.includes(me.department);
  const who = useWho();

  if (list.isPending) return <Skeleton rows={6} />;
  if (list.isError) return <div className="p-6"><Refusal failure={failureOf(list.error)} /></div>;
  const campaigns = list.data.campaigns;
  const breached = campaigns.filter((c) => c.endedReason === 'breach');

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker="Who we sell to"
        title="Campaigns"
        subtitle="What the department’s agent may send on its own, approved once by a named person. Each email is still judged as it leaves: the level earned, the hour where it lands, and who said stop."
      />

      {breached.length > 0 && (
        <div role="alert" className="flex flex-col gap-1 rounded-brand border border-danger p-4" data-testid="breach-banner">
          <Kicker tone="danger">Stopped by a threshold agreed in advance</Kicker>
          <p className="m-0 text-body">
            {breached.length === 1 ? `“${breached[0]!.name}” was` : `${breached.length} campaigns were`} ended when too many first emails bounced
            or were complained about, and the agent went back to drafts a person sends. Nothing already sent was recalled.{' '}
            {me.isSuperadmin ? (
              <Link className="text-accent underline" to="/incidents">
                The incident has the reading, in the words the threshold was agreed in.
              </Link>
            ) : (
              'A superadmin has the incident, with the reading in the words the threshold was agreed in.'
            )}
          </p>
        </div>
      )}

      <Table<WireCampaign>
        title="campaigns"
        blurb="A draft sends nothing. An approved campaign sends only what still qualifies when it goes."
        rows={campaigns}
        onOpen={(c) => setOpenId(c.id)}
        explain={(c) => explainCampaign(c, who, { department: me.department, mayDecide, onOpen: () => setOpenId(c.id) })}
        testId="campaigns"
        emptyTitle="No campaigns yet"
        emptyBody={mayDecide ? 'Draw one up below. Nothing is sent under it until it is approved.' : 'The department’s lead draws them up.'}
        columns={[
          { label: 'Campaign', get: (c) => c.name, primary: true },
          { label: 'Countries', get: (c) => countryNames(c.markets) },
          { label: 'Sent', get: (c) => `${c.sent} of ${c.ceiling}` },
          { label: 'Until', get: (c) => when(c.validTo) },
          { label: 'State', get: (c) => CAMPAIGN_CHIP[c.state].label, chip: (c) => CAMPAIGN_CHIP[c.state] },
        ]}
      />

      {openId && <CampaignRecord id={openId} mayDecide={mayDecide} onClose={() => setOpenId(null)} />}

      {mayDecide && <DrawUp />}

      <Sequences />
    </div>
  );
}

export function CampaignRecord({ id, mayDecide, onClose }: { id: string; mayDecide: boolean; onClose: () => void }) {
  const q = useCampaign(id);
  const end = useEndCampaign();
  const toast = useToast();
  const [approving, setApproving] = useState(false);
  if (q.isPending) return <Skeleton rows={4} />;
  if (q.isError) return <Refusal failure={failureOf(q.error)} compact />;
  const { campaign: c, sender, watchedSend } = q.data;
  const chip = CAMPAIGN_CHIP[c.state];
  return (
    <section className="flex flex-col gap-4 rounded-brand border border-strong p-5" data-testid="campaign-record">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-panel">{c.name}</h2>
        <Badge tone={chip.tone}>{chip.label}</Badge>
        <Button icon="close" variant="quiet" onClick={onClose}>Close</Button>
      </div>
      <FactList
        facts={[
          ['Countries', c.markets.join(', ')],
          ['Restaurants', `${c.minTier === 'strong' ? 'Strong fits only' : 'Strong and possible fits'}, to ${c.addressKinds.map((k) => (k === 'role' ? 'shared' : 'personal')).join(' or ')} addresses`],
          ['How many', `${c.sent} sent, ${c.planned} planned, ${c.remaining} left of ${c.ceiling}`],
          ['When', `${when(c.validFrom)} to ${when(c.validTo)}`],
          ['Approved', c.approvedBy ? `by ${c.approvedBy}${c.approvedAt ? `, ${when(c.approvedAt)}` : ''}` : null],
          ['Ended', c.endedReason ? `${CAMPAIGN_END_WORDS[c.endedReason]}${c.endedAt ? `, ${when(c.endedAt)}` : ''}` : null],
          ['Sends as', sender ? `“${sender.displayName}” from ${sender.address}` : 'Nobody is declared to send from the shared box'],
        ]}
      />
      <div className="flex flex-col gap-2">
        <Kicker>The email, as approved</Kicker>
        <p className="m-0 text-body font-semibold">{c.templateSubject}</p>
        <pre className="m-0 whitespace-pre-wrap rounded-brand border border-line bg-raised p-3 font-sans text-body" data-testid="campaign-template">
          {c.templateBody.replace('{{paragraph}}', `[ one paragraph about this restaurant, at most ${c.paragraphWords} words, written by the agent ]`)}
        </pre>
        <p className="m-0 text-dense text-secondary">The disclosure footer is added by the platform, in the recipient’s language and English. The agent writes nothing else.</p>
      </div>
      {mayDecide && c.state !== 'ended' && (
        <div className="flex flex-wrap gap-2">
          {c.state === 'draft' && (
            <Button icon="check" variant="primary" onClick={() => setApproving(true)}>Approve…</Button>
          )}
          <Button
            variant="danger"
            disabled={end.isPending}
            onClick={() =>
              end.mutate(c.id, {
                onSuccess: (r) => toast({ text: r.cancelled > 0 ? `Ended. ${r.cancelled} planned email(s) will not be sent.` : 'Ended.' }),
              })
            }
          >
            End it
          </Button>
        </div>
      )}
      {end.isError && <Refusal failure={failureOf(end.error)} compact />}
      {approving && <ApproveCampaign campaign={c} sender={sender} watchedSend={watchedSend} onClose={() => setApproving(false)} />}
    </section>
  );
}

function ApproveCampaign({
  campaign,
  sender,
  watchedSend,
  onClose,
}: {
  campaign: WireCampaign;
  sender: { address: string; displayName: string } | null;
  watchedSend: boolean;
  onClose: () => void;
}) {
  const me = useMeOrThrow();
  const approve = useApproveCampaign();
  const toast = useToast();
  const [token, setToken] = useState('');
  const [sawName, setSawName] = useState(false);
  const ready = sender !== null && watchedSend;
  return (
    <Dialog
      open
      danger
      onOpenChange={(o) => !o && onClose()}
      kicker="Critical · one token"
      title={`Approve “${campaign.name}”`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      note={!ready ? 'Not yet — see above' : !sawName ? 'Say that the name held' : token.trim() === '' ? 'The token first' : undefined}
      primary={{
        label: 'Spend the token and approve',
        disabled: !ready || !sawName || token.trim() === '' || approve.isPending,
        onClick: () =>
          approve.mutate(
            { campaignId: campaign.id, fromNameConfirmed: sawName, supervisionToken: token.trim() },
            {
              onSuccess: () => {
                toast({ text: `Approved. Up to ${campaign.ceiling} first emails may go until ${when(campaign.validTo)}, each judged as it leaves.` });
                onClose();
              },
            },
          ),
      }}
    >
      <p className="m-0 text-body">
        Once approved, the agent may send first emails under this template without a person pressing send on each — up to {campaign.ceiling}, to{' '}
        {campaign.markets.join(', ')}, until {when(campaign.validTo)}. It sends nothing while the department is still on drafts a person sends,
        and nothing to anybody who said stop.
      </p>
      {!sender && <p className="m-0 text-body text-danger">Nobody is declared to send from the shared box. That is a line in the department’s ledger file.</p>}
      {sender && !watchedSend && (
        <p className="m-0 text-body text-danger" data-testid="needs-watched-send">
          Nobody has yet sent one of the agent’s drafts from {sender.address} and watched it arrive. Do that once first: open a first email in
          Mail, send it, and look at it in the recipient’s inbox.
        </p>
      )}
      {sender && (
        <label className="flex min-h-tap items-start gap-2 text-body">
          <input type="checkbox" checked={sawName} onChange={(e) => setSawName(e.target.checked)} aria-label="The name held" />
          <span>I watched a send arrive, and it came from “{sender.displayName}” — not from the box’s own name.</span>
        </label>
      )}
      <TokenPanel operation="campaign.approve" target={`campaign:${campaign.id}`} token={token} onToken={setToken} canMint={me.isSuperadmin} />
      {approve.isError && <Refusal failure={failureOf(approve.error)} compact />}
    </Dialog>
  );
}

const DAY = 86_400_000;
const isoDate = localIsoDate;

function DrawUp() {
  const me = useMeOrThrow();
  const markets = useMarkets(me.department);
  const create = useCreateCampaign();
  const toast = useToast();
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('Hello,\n\n{{paragraph}}\n\nWould a sample be useful?');
  const [words, setWords] = useState(80);
  const [chosen, setChosen] = useState<string[]>([]);
  const [minTier, setMinTier] = useState<'strong' | 'possible'>('strong');
  const [personal, setPersonal] = useState(false);
  const [ceiling, setCeiling] = useState(20);
  const [from, setFrom] = useState(isoDate(new Date()));
  const [to, setTo] = useState(isoDate(new Date(Date.now() + 14 * DAY)));

  // Only a country the ledger has opened to a cold first email; the platform checks again.
  const open = (markets.data?.markets ?? []).filter(
    (m) => m.declared && m.policy?.enabled && (m.policy.legalBasis === 'opt_out_b2b' || m.policy.legalBasis === 'generic_address_only'),
  );
  const slots = body.split('{{paragraph}}').length - 1;
  const terms: CampaignTerms = {
    name: name.trim(),
    templateSubject: subject.trim(),
    templateBody: body,
    paragraphWords: words,
    markets: chosen,
    minTier,
    addressKinds: personal ? ['role', 'personal'] : ['role'],
    ceiling,
    validFrom: new Date(`${from}T00:00:00`).toISOString(),
    validTo: new Date(`${to}T23:59:59`).toISOString(),
  };
  const problem =
    !terms.name ? 'Give it a name' : !terms.templateSubject ? 'Write the subject' : slots !== 1 ? 'The email needs exactly one {{paragraph}}' : chosen.length === 0 ? 'Choose a country' : null;

  return (
    <section className="flex flex-col gap-3" aria-label="Draw up a campaign">
      <Kicker>Draw up a campaign</Kicker>
      {open.length === 0 && (
        <p className="m-0 text-body text-secondary" data-testid="no-open-country">
          No country is open to a first email yet. Opening one is a person’s commit to the markets file, with the legal basis confirmed.
        </p>
      )}
      <Field label="Name" hint="For the people who read this list.">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Stockholm tasting menus, autumn" />
      </Field>
      <Field label="Subject">
        <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder="Black garlic for your kitchen" />
      </Field>
      <Field label="The email" hint="Exactly one {{paragraph}}, which the agent writes for each restaurant. No links. The footer is added for you.">
        <Textarea
          aria-label="The email"
          className="min-h-[140px]"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <div className="flex flex-wrap gap-4">
        <Field label="Paragraph, at most" hint="20 to 120 words.">
          <TextInput type="number" min={20} max={120} value={words} onChange={(e) => setWords(Number(e.target.value))} aria-label="Words" />
        </Field>
        <Field label="How many, at most" hint="1 to 500.">
          <TextInput type="number" min={1} max={500} value={ceiling} onChange={(e) => setCeiling(Number(e.target.value))} aria-label="Ceiling" />
        </Field>
        <Field label="From">
          <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
        </Field>
        <Field label="Until" hint="Thirty days at most.">
          <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Until" />
        </Field>
      </div>
      <fieldset className="m-0 flex flex-wrap gap-3 border-0 p-0">
        <legend className="mb-1 text-label uppercase tracking-[0.1em] text-meta">Countries</legend>
        {open.map((m) => (
          <label key={m.code} className="flex min-h-tap items-center gap-2 text-body">
            <input
              type="checkbox"
              checked={chosen.includes(m.code)}
              onChange={(e) => setChosen((c) => (e.target.checked ? [...c, m.code] : c.filter((x) => x !== m.code)))}
            />
            {m.name}
          </label>
        ))}
      </fieldset>
      <div className="flex flex-wrap gap-4 text-body">
        <label className="flex min-h-tap items-center gap-2">
          <input type="checkbox" checked={minTier === 'possible'} onChange={(e) => setMinTier(e.target.checked ? 'possible' : 'strong')} />
          Possible fits too, not only strong ones
        </label>
        <label className="flex min-h-tap items-center gap-2">
          <input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} />
          A person’s address too, where the country allows it
        </label>
      </div>
      <div>
        <Button
          variant="primary"
          disabled={problem !== null || create.isPending}
          onClick={() => create.mutate(terms, { onSuccess: () => toast({ text: 'Drawn up. It sends nothing until it is approved.' }) })}
        >
          Draw it up
        </Button>
        {problem && <span className="ml-3 text-dense text-secondary">{problem}</span>}
      </div>
      {create.isError && <Refusal failure={failureOf(create.error)} compact />}
    </section>
  );
}
